/**
 * 网络工具函数
 * 提供错误处理、URL 构建等常用功能
 */

/**
 * HTTP 状态码常量
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
};

/**
 * 错误类型枚举
 */
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

/**
 * 网络错误类
 */
export class NetworkError extends Error {
  /**
   * 构造函数
   * @param {string} message - 错误消息
   * @param {string} type - 错误类型
   * @param {number} status - HTTP 状态码
   * @param {Object} details - 错误详情
   */
  constructor(message, type = ERROR_TYPES.UNKNOWN, status = null, details = null) {
    super(message);
    this.name = 'NetworkError';
    this.type = type;
    this.status = status;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * 解析网络错误
 * @param {Error} error - 原始错误对象
 * @returns {NetworkError} 解析后的网络错误
 */
export const parseNetworkError = (error) => {
  // ky 错误处理
  if (error.name === 'HTTPError') {
    const { response } = error;
    const status = response?.status;
    
    switch (status) {
      case HTTP_STATUS.BAD_REQUEST:
        return new NetworkError(
          '请求参数错误',
          ERROR_TYPES.VALIDATION,
          status,
          { originalError: error }
        );
      
      case HTTP_STATUS.UNAUTHORIZED:
        return new NetworkError(
          '身份验证失败',
          ERROR_TYPES.AUTHENTICATION,
          status,
          { originalError: error }
        );
      
      case HTTP_STATUS.FORBIDDEN:
        return new NetworkError(
          '权限不足',
          ERROR_TYPES.AUTHORIZATION,
          status,
          { originalError: error }
        );
      
      case HTTP_STATUS.NOT_FOUND:
        return new NetworkError(
          '请求的资源不存在',
          ERROR_TYPES.NOT_FOUND,
          status,
          { originalError: error }
        );
      
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        return new NetworkError(
          '服务器错误，请稍后重试',
          ERROR_TYPES.SERVER,
          status,
          { originalError: error }
        );
      
      default:
        return new NetworkError(
          `请求失败 (${status})`,
          ERROR_TYPES.UNKNOWN,
          status,
          { originalError: error }
        );
    }
  }
  
  // 超时错误
  if (error.name === 'TimeoutError') {
    return new NetworkError(
      '请求超时，请检查网络连接',
      ERROR_TYPES.TIMEOUT,
      null,
      { originalError: error }
    );
  }
  
  // 网络连接错误
  if (error.message?.includes('fetch')) {
    return new NetworkError(
      '网络连接失败，请检查网络设置',
      ERROR_TYPES.NETWORK,
      null,
      { originalError: error }
    );
  }
  
  // 其他错误
  return new NetworkError(
    error.message || '未知错误',
    ERROR_TYPES.UNKNOWN,
    null,
    { originalError: error }
  );
};

/**
 * 构建查询参数字符串
 * @param {Object} params - 参数对象
 * @returns {string} 查询参数字符串
 */
export const buildQueryString = (params) => {
  if (!params || typeof params !== 'object') {
    return '';
  }
  
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, String(item)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  
  return searchParams.toString();
};

/**
 * 构建完整的 URL
 * @param {string} baseUrl - 基础 URL
 * @param {string} path - 路径
 * @param {Object} params - 查询参数
 * @returns {string} 完整的 URL
 */
export const buildUrl = (baseUrl, path = '', params = {}) => {
  let url = baseUrl;
  
  // 添加路径
  if (path) {
    url = url.endsWith('/') ? url + path : url + '/' + path;
  }
  
  // 添加查询参数
  const queryString = buildQueryString(params);
  if (queryString) {
    url += (url.includes('?') ? '&' : '?') + queryString;
  }
  
  return url;
};

/**
 * 检查是否为有效的 URL
 * @param {string} url - 待检查的 URL
 * @returns {boolean} 是否为有效 URL
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @param {number} decimals - 小数位数
 * @returns {string} 格式化后的文件大小
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * 延迟函数
 * @param {number} ms - 延迟毫秒数
 * @returns {Promise} Promise 对象
 */
export const delay = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 重试函数
 * @param {Function} fn - 要重试的函数
 * @param {number} maxRetries - 最大重试次数
 * @param {number} delayMs - 重试间隔（毫秒）
 * @returns {Promise} Promise 对象
 */
export const retry = async (fn, maxRetries = 3, delayMs = 1000) => {
  let lastError;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (i < maxRetries) {
        await delay(delayMs * Math.pow(2, i)); // 指数退避
      }
    }
  }
  
  throw lastError;
};